import React, { useEffect, useState, useContext, useRef } from "react";
import axiosInstance from "../api/axios";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Ellipsis, MessageCircle, Send } from "lucide-react";
import { useModal } from "./ModalManager";
import { useCountdown } from "../hooks/useCountdown";
import ReactionPickerButton from "./ReactionPickerButton";
import ReactionCounts from "./ReactionCounts";
import { formatPostTimestamp } from "../utils/formatPostTimestamp";
import MentionText from "./mentions/MentionText";

// "nr_alice" → "NA", "alice" → "A".
function initialsFor(username = "") {
  const parts = username.split(/[\s._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("") || "?";
}

// Feed header avatar: the profile picture, or the user's initials when there
// is none or it fails to load.
function FeedAvatar({ url, username }) {
  const [failedUrl, setFailedUrl] = useState(null);
  const className = "w-7 h-7 rounded-full border shrink-0";

  if (!url || failedUrl === url) {
    return (
      <span
        aria-hidden="true"
        className={`${className} flex items-center justify-center bg-gray-200 text-[10px] font-semibold text-gray-600 select-none`}
      >
        {initialsFor(username)}
      </span>
    );
  }
  return <img src={url} alt="" onError={() => setFailedUrl(url)} className={`${className} object-cover`} />;
}

export default function PostCard({ postData, focusPost, onDeletePost, user, token }) {
  const [isSaved, setIsSaved] = useState(false);
  const [post, setPost] = useState(postData ? postData : null);
  const { label, isExpired } = useCountdown(postData?.expires_at);
  const { openModal, closeTopModal } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (!postData) return;
    setPost(postData);
  }, [postData]);

  const handleToggleLike = async () => {
    try {
      const res = await axiosInstance.post(`/posts/${post.id}/toggle_like/`, null, {
        headers: { Authorization: `Token ${token}` },
      });
      setPost((prev) => ({
        ...prev,
        is_liked: res.data.is_liked,
        likes_count: res.data.likes_count,
      }));
    } catch (err) {
      console.error("Failed to like/unlike:", err);
    }
  };

  const handleReacted = ({ my_reaction, reaction_counts }) => {
    setPost((prev) => ({
      ...prev,
      my_reaction,
      reaction_counts,
    }));
  };

  const toggleSave = async (postId) => {
    try {
      const res = await axiosInstance.post(
        `/saved/toggle/`,
        { post: postId },
        {
          headers: { Authorization: `Token ${token}` },
        },
      );
      if (res.data.status == "saved") {
        setIsSaved(true);
      } else {
        setIsSaved(false);
      }
    } catch (err) {
      console.error("Failed to save/unsave post");
    }
  };

  const getSavedStatus = async (postId) => {
    try {
      const res = await axiosInstance.get(`/saved/status`, {
        params: { post: postId },
        headers: { Authorization: `Token ${token}` },
      });
      if (res.data.is_saved) {
        setIsSaved(true);
      }
    } catch (err) {
      console.error("Failed to get status of saved post");
    }
  };

  function SocialContext({ socialContext }) {
    function ProfileBubble({ user }) {
      return (
        <div>
          <Link
            to={`/profiles/${user.username}`}
            className="flex items-center gap-2 min-w-0 flex-1"
            title={user?.username}
          >
            <FeedAvatar url={user.profile_pic_url} username={user.username} />
            <span className="font-semibold text-sm truncate whitespace-nowrap overflow-hidden">
              {user.username}
            </span>
          </Link>
        </div>
      );
    }

    if (!socialContext) return null;

    // Home is friends-only, so the context is "self" or "friend": always the author.
    return (
      <div>
        <ProfileBubble user={socialContext.post_user} />
      </div>
    );
  }

  function getTimeLeftPercent(expiresAt) {
    const now = Date.now();
    const end = new Date(expiresAt).getTime();
    const total = 24 * 60 * 60 * 1000; // example: 24h lifetime
    const remaining = Math.max(0, end - now);

    return Math.max(0, Math.min(100, (remaining / total) * 100));
  }

  function getTimerStyles(percentLeft) {
    if (percentLeft > 75) {
      return "border-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.25)]";
    }
    if (percentLeft > 40) {
      return "border-yellow-300 shadow-[0_0_12px_rgba(250,204,21,0.25)]";
    }
    if (percentLeft > 15) {
      return "border-orange-300 shadow-[0_0_12px_rgba(251,146,60,0.3)]";
    }
    return "border-red-500 shadow-[0_0_14px_rgba(239,68,68,0.35)]";
  }

  function getCountdownTextStyles(percentLeft) {
    if (percentLeft > 75) return "text-cyan-500";
    if (percentLeft > 40) return "text-yellow-500";
    if (percentLeft > 15) return "text-orange-500";
    return "text-red-500";
  }

  function formatRelative(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  // Explicit backend field — do not infer "indefinite" from !expires_at.
  const isIndefinite = post?.expiration_type === "indefinite";
  const percentLeft = getTimeLeftPercent(post?.expires_at);
  // expires_at is null for indefinite posts; new Date(null) resolves to the
  // Unix epoch, so getTimeLeftPercent would otherwise return 0 and paint
  // indefinite posts with the "about to expire" red border. Give them the
  // calm/neutral (100%-time-left) treatment instead.
  const timerStyles = post?.is_expired
    ? "border-gray-300 bg-gray-50"
    : isIndefinite
      ? getTimerStyles(100)
      : getTimerStyles(percentLeft);
  const textStyles = post?.is_expired ? "text-gray-500" : getCountdownTextStyles(percentLeft);

  function PostText({ text, mentions, limit = 140 }) {
    const [expanded, setExpanded] = useState(false);

    const shouldTruncate = text.length > limit;

    return (
      <>
        <span className="whitespace-pre-wrap break-words">
          {/* Truncates between mentions, never through one. */}
          <MentionText text={text} mentions={mentions} limit={!expanded && shouldTruncate ? limit : null} />
        </span>
        {shouldTruncate && (
          <button
            type="button"
            // onClick={() => setExpanded((prev) => !prev)}
            onClick={() => focusPost(post)}
            className="ml-1 text-blue-500 hover:text-blue-600"
          >
            {expanded ? "See less" : "See more"}
          </button>
        )}
      </>
    );
  }

  return (
    <div key={post.id} className="flex w-full max-w-[480px] mx-auto mb-6">
      <div className="flex flex-col w-full mb-4">
        <div className={`w-full border-2 mb-3 rounded-xl ${timerStyles}`}>
          {/* Post Card Top */}
          <div className="flex items-center gap-2 p-3 border-b border-gray-200">
            {/* Post Card Header */}
            {post?.social_context ? (
              <SocialContext socialContext={post.social_context} />
            ) : (
              <div>
                <Link
                  to={`/profiles/${post.username}`}
                  className="flex items-center gap-2 min-w-0 flex-1"
                  title={post?.username}
                >
                  <FeedAvatar url={post.profile_pic_url} username={post.username} />
                  <span className="font-semibold text-sm truncate whitespace-nowrap overflow-hidden">
                    {post.username}
                  </span>
                </Link>
              </div>
            )}

            {/* Options button pinned right */}
            <button
              onClick={() =>
                openModal("PostOptionsModal", {
                  post,
                  isOwner: post.username === user.username,
                  onDelete: async () => {
                    await onDeletePost(post.id);
                    closeTopModal();
                  },
                })
              }
              className="ml-auto text-gray-600 shrink-0"
              aria-label="Post options"
            >
              <Ellipsis />
            </button>
          </div>
          {/* Post Card media */}
          {post.media_type != "text" && (
            <div className="w-full max-w-[480px] h-[80vh] shadow flex items-center justify-center">
              {post.media_type === "image" ? (
                <img
                  src={post.media_url}
                  alt="Post"
                  className="max-h-[80vh] max-w-full object-contain"
                />
              ) : (
                <video
                  controls
                  className="w-full h-[80vh] shadow flex items-center justify-center"
                  muted
                >
                  <source src={post.media_url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}

          {/* Post card bottom */}
          <div className="flex flex-col min-h-[120px] shadow p-3 border-t border-gray-200">
            {/* text + tags */}
            <div className="flex flex-1 flex-col w-full pb-1">
              {/* text */}
              <div>
                {post.text && (
                  <div className="w-full text-sm">
                    <span className="font-semibold">{post.username} </span>
                    <PostText text={post.text} mentions={post.mentions} inline />
                  </div>
                )}
              </div>

              {/* tags */}
              <div className="mt-auto flex flex-wrap gap-2 pt-2">
                {post.tags?.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => navigate(`/tags/${tag}`)}
                    className="text-sm px-2 py-1 rounded-full border"
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            {/* expiration timer */}
            <div className="flex w-full space-x-1 justify-end pt-2 border-t border-gray-200">
              {post.is_expired ? (
                <div className="flex space-x-1 items-center">
                  <p className="text-xs text-gray-500 font-medium">Expired</p>
                  <p className={`text-xs ${textStyles}`}>{formatRelative(post.expires_at)}</p>
                </div>
              ) : isIndefinite ? (
                <div className="flex space-x-1 items-center">
                  <p className="text-xs text-gray-500">{formatPostTimestamp(post.created_at)}</p>
                </div>
              ) : (
                <div className="flex space-x-1 items-center">
                  <p className="text-xs text-gray-500">Expires in:</p>
                  <p className={`text-xs ${textStyles}`}>{label}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="flex space-x-1 items-center ml-2">
              <button
                onClick={post.is_expired ? undefined : handleToggleLike}
                disabled={post.is_expired}
                className={`flex flex-col items-center ${
                  post.is_expired
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-red-500 hover:text-red-600"
                }`}
              >
                <Heart
                  fill={!post.is_expired && (post?.is_liked || false) ? "currentColor" : "none"}
                />
              </button>
              <span>{post.likes_count}</span>
            </div>
            <div className="flex space-x-1 items-center">
              <button onClick={() => focusPost(post)}>
                <MessageCircle />
              </button>
              {/* </button> */}
              <span>{post.comment_count}</span>
            </div>
            {/* <button className="flex flex-col items-center mb-3" onClick={() => toggleSave(post.id)}>
            <Bookmark className={isSaved ? "fill-yellow-500" : "fill-none"} />
          </button> */}
            <ReactionCounts reactionCounts={post.reaction_counts} />
          </div>
          <div className="flex items-center gap-2 mr-2">
            <ReactionPickerButton
              postId={post.id}
              token={token}
              myReaction={post.my_reaction}
              onReacted={handleReacted}
              disabled={post.is_expired}
            />
            <button
              className={`flex items-center ${
                post.is_expired ? "text-gray-400 cursor-not-allowed" : ""
              }`}
              onClick={
                post.is_expired
                  ? undefined
                  : () => {
                      openModal("ShareModal", { post });
                    }
              }
              disabled={post.is_expired}
            >
              <Send />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
